import React, {ReactNode} from "react";
import {Button} from "primereact/button";
import {
	Information,
	Parameter as ParameterType,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import AbstractDialog, {
	AbstractDialogProperties,
	AbstractDialogState
} from "../../../../shared/dialog/components/AbstractDialog";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import Parameter from "../../../dialog/parameter/Parameter";
import PortalContext from "../../../PortalContext";
import PortalFields from "../../../dialog/field/PortalFields";
import PortalDialogFields from "../../../dialog/PortalDialogFields";
import PortalDocument from "../../PortalDocument";
import PortalDialogDefault from "../../../dialog/parameter/PortalDialogDefault";
import Translate from "../../../../shared/translation/Translate";
import Exception from "../../../../shared/exception/Exception";
import ErrorCode from "../../../../shared/exception/ErrorCode";
import DocumentSettings from "../../DocumentSettings";
import PasswordDialog from "../../../dialog/components/security/PasswordDialog";
import PortalDialogType from "../../../dialog/PortalDialogType";
import {DebugGlobals} from "../../../dialog/components/settings/DebugDialog";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import {initAreaLayerGlobals} from "../../../viewer/pdf/AreaLayerInterface";
import {initFormLayerGlobals} from "../../../viewer/pdf/form/FormLayerInterface";

export interface SidebarFunctionProperties<PARAMETER_TYPE extends ParameterType> extends AbstractDialogProperties,
	I18nGlobals, DebugGlobals {
	parameter?: Parameter<PARAMETER_TYPE>;
	context: PortalContext;
	updated?: number;
}

export interface SidebarFunctionState<PARAMETER_TYPE extends ParameterType> extends AbstractDialogState {
	parameter?: Parameter<PARAMETER_TYPE>;
	fields?: PortalFields;
	fieldErrors: Map<any, string>;
	showPasswordDialog: boolean;
}

export default abstract class SidebarFunction<PARAMETER_TYPE extends ParameterType = any,
	PROPERTY_TYPE extends SidebarFunctionProperties<PARAMETER_TYPE> = any,
	STATUS_TYPE extends SidebarFunctionState<PARAMETER_TYPE> = any> extends AbstractDialog<PROPERTY_TYPE, STATUS_TYPE> {
	/**
	 * Initialize an AbstractDialog component instance
	 */
	constructor(props: Readonly<PROPERTY_TYPE>) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<any, string>(),
			showPasswordDialog: false,
			parameter: this.getDefaultParameter(),
			fields: PortalDialogFields.fields.get(this.getDialogType())?.getDefaults()
		} as STATUS_TYPE;

		this.hide = this.hide.bind(this);
		this.onShow = this.onShow.bind(this);
		this.onError = this.onError.bind(this);
		this.renderError = this.renderError.bind(this);
		this.renderInfo = this.renderInfo.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		if (this.props.visible) {
			await this.onShow();
		}
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<PROPERTY_TYPE>, prevState: Readonly<STATUS_TYPE>, snapshot?: any
	): Promise<void> {
		if (prevProps.visible !== this.props.visible && this.props.visible) {
			await this.onShow();
		}
	}

	/**
	 * Check if the dialog is enabled
	 */
	public static isEnabled(portalInfo: Information): boolean {
		return true;
	}

	/**
	 * Check if the dialog is active for the given documents
	 */
	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		return this.isEnabled(portalInfo);
	}

	/**
	 * Handle the dialog submission
	 */
	protected async submit(): Promise<void> {
		await super.submit();

		this.props.context.getPage().setState({
			lastSelection: null
		});

		let selection: Array<PortalDocument> = this.props.context.documentManager.getSelection();
		PortalDialogDefault.setParameter(this.getDialogType(), this.state.parameter!, selection);
	}

	/**
	 * Handle closing/hiding the dialog
	 */
	protected hide(): void {
		this.state.fieldErrors.clear();

		this.setState({
			submitted: false,
			showPasswordDialog: false,
			exception: undefined,
			info: undefined
		});

		let selection: Array<PortalDocument> = this.props.context.documentManager.getSelection();
		for (let selected of selection) {
			let documentSettings: DocumentSettings = selected.getSettings();
			documentSettings.showSidebarFunction = false;
		}

		this.props.context.documentManager.updateGlobalDocumentList();
		this.props.hide();
	}

	/**
	 * Handle canceling the dialog
	 */
	protected cancel(): void {
		let selection: Array<PortalDocument> = this.props.context.documentManager.getSelection();

		for (let selected of selection) {
			let documentSettings: DocumentSettings = selected.getSettings();
			documentSettings.resetMouseMode();
		}

		this.props.context.getPage().setState({
			...initAreaLayerGlobals(),
			...initFormLayerGlobals()
		});

		this.props.context.documentManager.updateGlobalDocumentList();
		this.hide();
	}

	/**
	 * Render the dialog footer
	 */
	protected renderFooter(): ReactNode {
		return (
			<div className="function-footer toolbar inverted">
				<div className="p-col-12 p-grid">
					<Button label={Translate.value("buttons.submit")} disabled={!this.isValid()}
							icon={"icon" + (this.state.submitted ? " icon-spinner spin" : " icon-check")}
							className="primary-button" onClick={() => this.submit()}/>
					{this.state.submitted ?
						<Button label={Translate.value("buttons.close")} icon="icon icon-close"
								onClick={() => this.cancel()} className="dialog-button"/> :
						<Button label={Translate.value("buttons.cancel")} icon="icon icon-close"
								onClick={() => this.cancel()} className="dialog-button"/>
					}
				</div>
			</div>
		);
	}

	protected isValid(): boolean {
		if (!this.mounted || !this.props.visible) {
			return false;
		}

		let errors: Map<any, string> = this.state.fieldErrors;
		return errors.size === 0 && !this.state.submitted;
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return portalDocument.hasPasswordProtection("permission") &&
			(
				!portalDocument.getDocumentFile()?.password?.isPermissionSet ||
				portalDocument.hasError()
			);
	}

	protected async handleKeydown(event: KeyboardEvent): Promise<void> {
		if (event.key.toLowerCase() === "escape" && !this.state.showPasswordDialog) {
			await this.cancel();
		} else if (event.key.toLowerCase() === "enter" && this.isValid() && !this.state.showPasswordDialog &&
			!this.state.submitted) {
			await this.submit();
		}
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		let selectedDocuments: Array<PortalDocument> = this.props.context.documentManager.getSelection();
		let isEditPasswordNeeded: boolean = false;

		for (let portalDocument of selectedDocuments) {
			if (this.isEditPasswordNeeded(portalDocument)) {
				isEditPasswordNeeded = true;
				break;
			}
		}

		await new Promise((resolve) => {
			this.setState({
				parameter: FunctionHelper.applyValue(
					this.props.parameter, this.getDefaultParameter()
				),
				fields: PortalDialogFields.fields.get(this.getDialogType())?.get(
					this.props.context.documentManager.getSelection()
				),
				showPasswordDialog: isEditPasswordNeeded
			}, () => {
				resolve(undefined);
			});
		})
	};

	/**
	 * Handles a dialog exception and returns if the operation can be retried
	 */
	protected async onError(exception: Error): Promise<boolean> {
		let portalException: Exception = Exception.parseException(exception);

		if (portalException.name === "ERR_NETWORK") {
			await this.props.context.session.reconnectServer();
			return false;
		}

		switch (portalException.code) {
			case ErrorCode.PDF_PASSWORD_OWNER:
				this.setState({
					submitted: false,
					showPasswordDialog: true,
					exception: portalException
				});
				break;
			default:
				return super.onError(exception);
		}

		return false;
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (this.state.showPasswordDialog) {
			return <PasswordDialog {...this.props} visible={this.state.showPasswordDialog}
								   portalDocument={this.props.context.documentManager.getSelection()[0]}
								   close={this.hide} exception={this.state.exception}
								   hide={() => this.setState({
									   exception: undefined,
									   info: undefined,
									   showPasswordDialog: false
								   })}/>;
		}

		return super.render();
	}

	/**
	 * render component as react node
	 */
	abstract renderDialog(): ReactNode;

	/**
	 * Returns the dialog type
	 */
	protected abstract getDialogType(): PortalDialogType;

	/**
	 * Returns the dialog parameters
	 */
	protected abstract getDefaultParameter(): Parameter<PARAMETER_TYPE> | undefined;
}